import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import type { AuthResponse, UserGender } from '../api/types';
import {
  clearTokens,
  deleteAllMyPushTokens,
  loadStoredAccess,
  loadStoredRefresh,
  loadStoredUser,
  loginRequest,
  logoutRequest,
  maybeWipeKeychainOnFirstLaunch,
  saveTokens,
  saveUserProfile,
  setAuthInvalidHandler,
  setStoredProfileChangedHandler,
  trySilentRefresh,
  updateTimeZoneOnServer,
} from '../api/client';
import { deviceTimeZone } from '../util/organizerDateTime';
import { resetPushNotifyDedupeState } from '../notifications/pushDedupe';
import { refreshFcmRegistration, syncFcmTokenToBackend } from '../notifications/push';
import { setupIosVoipPush } from '../notifications/iosVoipPush';
import {
  loadAuthPushSubscriptions,
  teardownPushSubscriptions,
  type PushSubscriptionUnsubs,
} from './authPushSubscriptions';
import { TimeoutError, withTimeout } from '../util/withTimeout';
import { resetAnalyticsAfterLogout, track } from '../observability/analytics';
import { setCrashUser, trace } from '../observability/crashReporting';

/**
 * Что правит форма профиля. Ручка `PATCH /api/users/me` перезаписывает профиль целиком,
 * поэтому здесь перечислены все её поля разом: пропущенное поле — это не «не трогать», а
 * «стереть», и на стороне вызывающего это должно быть видно по типу.
 */
type ProfilePatch = {
  login: string;
  displayName: string;
  bio?: string | null;
  statusText?: string | null;
  gender?: UserGender | null;
  /** ISO `yyyy-MM-dd` или null. */
  birthDate?: string | null;
};

type AuthState = {
  ready: boolean;
  user:
    | Pick<
        AuthResponse,
        | 'userId'
        | 'login'
        | 'displayName'
        | 'uiLocale'
        | 'appRole'
        | 'hasAvatar'
        | 'avatarRevision'
        | 'avatarShape'
        | 'publicHandle'
        | 'profilePublic'
        | 'allowDiscoveryByHandle'
        | 'bio'
        | 'statusText'
        | 'doNotDisturb'
        | 'showMessageText'
        | 'gender'
        | 'birthDate'
      >
    | null;
  accessToken: string | null;
};

type AuthContextValue = AuthState & {
  login: (username: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  invalidateSession: () => void;
  applyAuth: (res: AuthResponse) => Promise<void>;
  /** Updates in-memory access JWT after a silent refresh (storage already updated). */
  applyAccessToken: (accessToken: string) => void;
  mergeUserLocale: (uiLocale: string) => Promise<void>;
  mergeUserProfile: (patch: ProfilePatch) => Promise<void>;
  mergeUserShareSettings: (patch: {
    publicHandle?: string | null;
    profilePublic?: boolean;
    allowDiscoveryByHandle?: boolean;
  }) => Promise<void>;
  /** «Не беспокоить»: сохраняется своей ручкой, поэтому и в стор кладётся отдельно. */
  mergeUserDoNotDisturb: (doNotDisturb: boolean) => Promise<void>;
  /** «Показывать текст в уведомлениях» (PRIV-5) — тоже своей ручкой. */
  mergeUserShowMessageText: (showMessageText: boolean) => Promise<void>;
  mergeUserAvatar: (patch: {
    hasAvatar: boolean;
    avatarRevision: number;
    avatarShape?: string | null;
  }) => Promise<void>;
};

const Ctx = createContext<AuthContextValue | undefined>(undefined);

/** iOS Keychain / RNFS can wedge after force-quit on some OS builds — never block `ready` forever. */
const BOOT_STORAGE_TIMEOUT_MS = 12_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthContextValue['user']>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [lastPushSyncAt, setLastPushSyncAt] = useState(0);
  const pushSubsRef = useRef<PushSubscriptionUnsubs>({});

  /**
   * Cold-start auth.
   *
   * IMPORTANT: this path is on the critical render path. It MUST NOT wait on
   * the network. On iOS we previously blocked here on `trySilentRefresh()` +
   * `probeAuth()`, and on a flaky 4G connection `fetch()` hangs (no built-in
   * timeout in React Native), so `setReady(true)` never fired and the app
   * was stuck on a blank dark canvas with no UI underneath. The user could
   * only "get in" through deep-links from push notifications.
   *
   * Build 8 behaviour: read stored tokens, render the UI immediately. If the
   * access token turns out to be expired, the very next `apiFetch()` will
   * 401 and silently refresh (or fall through to `notifyAuthInvalid` →
   * logout). Refresh is also attempted in the background here as a courtesy
   * so the first protected request usually already has a fresh token.
   *
   * Storage reads are wrapped in {@link withTimeout} (see `BOOT_STORAGE_TIMEOUT_MS`)
   * so a wedged Keychain cannot leave the app stuck on the boot spinner forever.
   */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await withTimeout(
          (async () => {
            // iOS Keychain entries survive uninstall by default — wipe them on
            // a fresh install before we read any tokens, so a reinstalled app
            // doesn't boot with credentials from a previous session that the
            // server has already invalidated (which would manifest as 401 loops
            // and a frozen UI on the chat list).
            try {
              await maybeWipeKeychainOnFirstLaunch();
            } catch {
              /* swallow — fall through to normal boot */
            }
            const [storedAccess, storedUser] = await Promise.all([
              loadStoredAccess(),
              loadStoredUser(),
            ]);
            if (!alive) return;
            setAccessToken(storedAccess);
            setUser(storedUser);
          })(),
          BOOT_STORAGE_TIMEOUT_MS,
        );
      } catch (e) {
        if (e instanceof TimeoutError) {
          console.warn(
            '[AuthContext] Boot storage timed out (Keychain/RNFS hang?) — showing login; user can sign in again',
          );
        }
        // Storage read errors — start unauthenticated, user can re-login.
      } finally {
        if (alive) setReady(true);
      }

      // Background refresh: never gates UI. If the device-bound refresh token
      // is still valid we silently rotate the access JWT; if the server
      // explicitly says the refresh is invalid we log the user out; transient
      // errors (network/5xx) are ignored — the next protected call will retry
      // on demand via apiFetch's refresh-on-401 path.
      try {
        const refresh = await loadStoredRefresh();
        if (!refresh) return;
        const silent = await trySilentRefresh();
        if (!alive) return;
        if (silent.status === 'refreshed') {
          setAccessToken(silent.accessToken);
        } else if (silent.status === 'invalid_refresh') {
          setAccessToken(null);
          setUser(null);
        }
      } catch {
        /* transient — leave stored access in place */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Часовой пояс устройства — на сервер, молча, при каждом появлении сессии.
   *
   * <p>Не настройка человека, а факт о его телефоне, и обновляться он должен сам: без него
   * сервер не знает, когда у получателя утро, и напоминание о дне рождения контакта приходило
   * бы всем в один час по времени сервера. Тем же значением подставляется зона по умолчанию
   * новому напоминанию.
   *
   * <p>Отдельным эффектом от подписок на пуши: у тех есть отмена и teardown, а здесь один
   * запрос, который либо прошёл, либо повторится при следующем старте.
   */
  useEffect(() => {
    if (!ready || !accessToken) return;
    void updateTimeZoneOnServer(deviceTimeZone());
  }, [ready, accessToken]);

  useEffect(() => {
    if (!ready || !accessToken) return;
    const ac = new AbortController();
    pushSubsRef.current = {};
    void (async () => {
      try {
        const u = await loadAuthPushSubscriptions(ac.signal, {
          syncFcmTokenToBackend,
          setupIosVoipPush,
        });
        if (ac.signal.aborted) {
          teardownPushSubscriptions(u);
          return;
        }
        pushSubsRef.current = u;
        setLastPushSyncAt(Date.now());
      } catch (e) {
        console.warn('[AuthContext] push subscription bootstrap failed', e);
      }
    })();
    return () => {
      ac.abort();
      teardownPushSubscriptions(pushSubsRef.current);
      pushSubsRef.current = {};
    };
  }, [ready, accessToken]);

  useEffect(() => {
    if (!ready || !accessToken) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      const now = Date.now();
      if (now - lastPushSyncAt < 5 * 60 * 1000) return;
      void (async () => {
        try {
          await refreshFcmRegistration();
          setLastPushSyncAt(Date.now());
        } catch {
          /* noop */
        }
      })();
    });
    return () => sub.remove();
  }, [ready, accessToken, lastPushSyncAt]);

  const applyAuth = useCallback(async (res: AuthResponse) => {
    await saveTokens(res.accessToken, res.refreshToken);
    const profile = {
      userId: String(res.userId),
      login: res.login ?? '',
      displayName: res.displayName,
      appRole: res.appRole ?? 'USER',
      uiLocale: res.uiLocale ?? undefined,
      email: res.email,
      hasAvatar: res.hasAvatar === true,
      avatarRevision: typeof res.avatarRevision === 'number' ? res.avatarRevision : Number(res.avatarRevision) || 0,
      avatarShape: res.avatarShape ?? null,
      publicHandle: res.publicHandle ?? undefined,
      profilePublic: typeof res.profilePublic === 'boolean' ? res.profilePublic : true,
      allowDiscoveryByHandle:
        typeof res.allowDiscoveryByHandle === 'boolean' ? res.allowDiscoveryByHandle : true,
      bio: res.bio ?? null,
      statusText: res.statusText ?? null,
      // Старый сервер поля не пришлёт вовсе — молчания по умолчанию быть не должно.
      doNotDisturb: res.doNotDisturb === true,
      // Здесь наоборот: старый сервер текст показывает, и тумблер должен говорить то же.
      showMessageText: res.showMessageText !== false,
      gender: res.gender ?? null,
      birthDate: res.birthDate ?? null,
    };
    await saveUserProfile(profile);
    setAccessToken(res.accessToken);
    setUser(profile);
    // OBS-2. С этого момента отчёт о поломке знает, чей он: без этого падение на телефоне
    // невозможно связать с жалобой конкретного тестера, а разбор с этого и начинается.
    setCrashUser(profile.userId);
    trace('auth', 'вход выполнен');
  }, []);

  const applyAccessToken = useCallback((token: string) => {
    setAccessToken(token);
  }, []);

  const mergeUserLocale = useCallback(async (uiLocale: string) => {
    const prev = await loadStoredUser();
    if (!prev) return;
    const next = { ...prev, uiLocale };
    await saveUserProfile(next);
    setUser((u) => (u && u.userId === prev.userId ? { ...u, uiLocale } : u));
  }, []);

  const mergeUserProfile = useCallback(async (patch: ProfilePatch) => {
    const prev = await loadStoredUser();
    if (!prev) return;
    // Одним объектом, а не перечислением полей дважды: раньше список полей был выписан и в
    // сохраняемом профиле, и в состоянии, и разъезжались они молча — новое поле, забытое во
    // второй половине, живёт до перезапуска приложения и исчезает.
    const changes = {
      login: patch.login,
      displayName: patch.displayName,
      bio: patch.bio ?? null,
      statusText: patch.statusText ?? null,
      gender: patch.gender ?? null,
      birthDate: patch.birthDate ?? null,
    };
    await saveUserProfile({ ...prev, ...changes });
    setUser((u) => (u && u.userId === prev.userId ? { ...u, ...changes } : u));
  }, []);

  const mergeUserShareSettings = useCallback(
    async (patch: {
      publicHandle?: string | null;
      profilePublic?: boolean;
      allowDiscoveryByHandle?: boolean;
    }) => {
      const prev = await loadStoredUser();
      if (!prev) return;
      const next = { ...prev, ...patch };
      await saveUserProfile(next);
      setUser((u) => (u && u.userId === prev.userId ? { ...u, ...patch } : u));
    },
    [],
  );

  const mergeUserDoNotDisturb = useCallback(async (doNotDisturb: boolean) => {
    const prev = await loadStoredUser();
    if (!prev) return;
    await saveUserProfile({ ...prev, doNotDisturb });
    setUser((u) => (u && u.userId === prev.userId ? { ...u, doNotDisturb } : u));
  }, []);

  const mergeUserShowMessageText = useCallback(async (showMessageText: boolean) => {
    const prev = await loadStoredUser();
    if (!prev) return;
    await saveUserProfile({ ...prev, showMessageText });
    setUser((u) => (u && u.userId === prev.userId ? { ...u, showMessageText } : u));
  }, []);

  const mergeUserAvatar = useCallback(
    async (patch: { hasAvatar: boolean; avatarRevision: number; avatarShape?: string | null }) => {
      const prev = await loadStoredUser();
      if (!prev) return;
      const next = {
        ...prev,
        hasAvatar: patch.hasAvatar,
        avatarRevision: patch.avatarRevision,
        avatarShape: patch.avatarShape ?? prev.avatarShape,
      };
      await saveUserProfile(next);
      setUser((u) =>
        u && u.userId === prev.userId
          ? {
              ...u,
              hasAvatar: patch.hasAvatar,
              avatarRevision: patch.avatarRevision,
              avatarShape: patch.avatarShape ?? u.avatarShape,
            }
          : u,
      );
    },
    [],
  );

  const login = useCallback(
    async (username: string, password: string, captchaToken?: string) => {
      const res = await loginRequest(username.trim(), password, captchaToken);
      await applyAuth(res);
      track('login_success');
    },
    [applyAuth],
  );

  const logout = useCallback(async () => {
    resetPushNotifyDedupeState();
    void deleteAllMyPushTokens();
    track('logout');
    // Пока токен ещё есть: приёмник аналитики берёт пользователя из него, и накопленное после
    // clearTokens уехало бы уже от чужого имени — или не уехало бы вовсе.
    await resetAnalyticsAfterLogout();
    // Сначала гасим сессию на сервере — после clearTokens предъявлять уже нечего.
    // Ждём, но не полагаемся: logoutRequest сам глотает ошибки сети.
    await logoutRequest();
    await clearTokens();
    setAccessToken(null);
    setUser(null);
    // Снимаем сразу: отчёт с устройства, с которого человек вышел, не должен приписываться ему.
    trace('auth', 'выход выполнен');
    setCrashUser(null);
  }, []);

  const invalidateSession = useCallback(() => {
    void logout();
  }, [logout]);

  useEffect(() => {
    setAuthInvalidHandler(() => {
      invalidateSession();
    });
    return () => setAuthInvalidHandler(null);
  }, [invalidateSession]);

  /**
   * Профиль, приехавший вместе с обновлением токена, — на экран, а не только в хранилище.
   *
   * <p>Аватар меняют на одном устройстве, а видеть его должны все: `avatarRevision` — единственное,
   * по чему бейдж понимает, что картинку пора перекачать. Своего профиля с сервера клиент отдельно
   * не спрашивает, и новая ревизия приходит только в ответе `POST /api/auth/refresh`. Пока она
   * ложилась молча в хранилище, открытая вкладка веба показывала старый снимок до второй
   * перезагрузки — первая читала профиль, сохранённый прошлым запуском.
   *
   * <p>Сверка `userId` — на случай, когда обновление прилетело уже после выхода и входа под другим
   * человеком: чужой профиль на экране хуже устаревшего.
   */
  useEffect(() => {
    setStoredProfileChangedHandler((fresh) => {
      setUser((u) => {
        if (!u || u.userId !== fresh.userId) return u;
        // Поимённо, а не разворотом: в хранилище поля необязательные, и `{...u, ...fresh}`
        // затёр бы `hasAvatar`/`avatarRevision` значением `undefined` там, где сервер их не
        // прислал, — бейдж потерял бы аватар вместо того, чтобы его обновить.
        return {
          ...u,
          login: fresh.login || u.login,
          displayName: fresh.displayName || u.displayName,
          appRole: fresh.appRole ?? u.appRole,
          uiLocale: fresh.uiLocale ?? u.uiLocale,
          hasAvatar: fresh.hasAvatar ?? u.hasAvatar,
          avatarRevision: fresh.avatarRevision ?? u.avatarRevision,
          avatarShape: fresh.avatarShape ?? u.avatarShape,
          publicHandle: fresh.publicHandle ?? u.publicHandle,
          profilePublic: fresh.profilePublic ?? u.profilePublic,
          allowDiscoveryByHandle: fresh.allowDiscoveryByHandle ?? u.allowDiscoveryByHandle,
          bio: fresh.bio ?? u.bio,
          statusText: fresh.statusText ?? u.statusText,
        };
      });
    });
    return () => setStoredProfileChangedHandler(null);
  }, []);

  const value = useMemo(
    () => ({
      ready,
      user,
      accessToken,
      login,
      logout,
      invalidateSession,
      applyAuth,
      applyAccessToken,
      mergeUserLocale,
      mergeUserProfile,
      mergeUserShareSettings,
      mergeUserDoNotDisturb,
      mergeUserShowMessageText,
      mergeUserAvatar,
    }),
    [
      ready,
      user,
      accessToken,
      login,
      logout,
      invalidateSession,
      applyAuth,
      applyAccessToken,
      mergeUserLocale,
      mergeUserProfile,
      mergeUserShareSettings,
      mergeUserDoNotDisturb,
      mergeUserShowMessageText,
      mergeUserAvatar,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
